import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Nav, NavItem, NavLink, Collapse, Spinner, Alert } from "reactstrap";
import { ChevronDown, ChevronUp } from "lucide-react";
import CourseService from "../../../services/CourseService";
import icon from "../../../assets/kangaroo/quiz.png"

export default function CourseSidebar({ activeId, onSelectChild }) {
  const { id } = useParams();

  const { data: course, isLoading } = useQuery({
    queryKey: ["courseDetail", id],
    queryFn: () => CourseService.getDetails(id),
    enabled: !!id,
  });

  // openMap stores which accordion IDs are open
  const [openMap, setOpenMap] = useState({});

  // Only one section open at a time per level — pass the parentId to scope collapses
  const toggle = (itemId, parentId = null) => {
    setOpenMap((prev) => {
      const next = { ...prev };

      if (parentId) {
        // Close all siblings under same parent
        Object.keys(next).forEach((key) => {
          if (
            key.startsWith(`${parentId}__`) &&
            key !== `${parentId}__${itemId}`
          ) {
            next[key] = false;
          }
        });
        const scopedKey = `${parentId}__${itemId}`;
        next[scopedKey] = !prev[scopedKey];
      } else {
        // Top-level: close all other top-level items
        Object.keys(next).forEach((key) => {
          if (!key.includes("__") && key !== itemId) {
            next[key] = false;
          }
        });
        next[itemId] = !prev[itemId];
      }

      return next;
    });
  };

  const isOpen = (itemId, parentId = null) => {
    if (parentId) return openMap[`${parentId}__${itemId}`] ?? false;
    return openMap[itemId] ?? false;
  };

  // activeId for quiz nodes starts with "quiz-", for regular nodes it's a plain UUID
  const isActive = (itemId) => activeId === itemId;
  const isQuizActive = (itemId) => activeId === `quiz-${itemId}`;

  const sortByOrder = (arr = []) =>
    [...arr].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

  // Auto-expand the parent section that contains the active item
  useEffect(() => {
    if (!activeId || !course?.children) return;

    // Strip "quiz-" prefix to find real item id
    const realId = activeId.startsWith("quiz-")
      ? activeId.replace("quiz-", "")
      : activeId;

    course.children.forEach((child) => {
      // Check textFields directly on child
      const inChildTextFields = child.textFields?.some(
        (tf) => tf.id === realId,
      );
      if (inChildTextFields || child.id === realId || child.id === realId) {
        setOpenMap((prev) => ({ ...prev, [child.id]: true }));
      }

      // Check subDetails
      child.childrenSubDetails?.forEach((sub) => {
        const inSubTextFields = sub.textFields?.some((tf) => tf.id === realId);
        if (inSubTextFields || sub.id === realId) {
          setOpenMap((prev) => ({
            ...prev,
            [child.id]: true,
            [`${child.id}__${sub.id}`]: true,
          }));
        }
      });
    });
  }, [activeId, course]);

  // ───────────── Styles ─────────────

  const sidebarStyle = {
    width: "260px",
    height: "100%", // important
    overflowY: "auto", // important
    background: "linear-gradient(180deg, #7b1fa2, #6a1b9a)",
    color: "#fff",
    paddingTop: "20px",
    boxShadow: "2px 0 10px rgba(0,0,0,0.1)",
  };

  const baseItem = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "6px",
    transition: "all 0.2s ease",
    cursor: "pointer",
    color: "#fff",
  };

  const level1Style = {
    ...baseItem,
    padding: "10px 14px",
    fontSize: "15px",
    fontWeight: 500,
  };

  const level2Style = {
    ...baseItem,
    padding: "8px 14px 8px 28px",
    fontSize: "14px",
  };

  const level3Style = {
    ...baseItem,
    padding: "6px 14px 6px 50px",
    fontSize: "13px",
  };

  const activeStyle = {
    backgroundColor: "#ffffff22",
    borderLeft: "4px solid #fff",
  };

  const hoverEffect = (e, active) => {
    if (!active) e.currentTarget.style.background = "#ffffff15";
  };

  const removeHover = (e, active) => {
    if (!active) e.currentTarget.style.background = "transparent";
  };

  const Arrow = ({ open }) =>
    open ? <ChevronUp size={14} /> : <ChevronDown size={14} />;

  // ───────────── Text Fields ─────────────

  const renderTextFields = (textFields = [], parentSub, parentChild) =>
    textFields.map((tf) => {
      const active = isActive(tf.id);
      return (
        <NavItem key={tf.id}>
          <NavLink
            style={{ ...level3Style, ...(active && activeStyle) }}
            onMouseEnter={(e) => hoverEffect(e, active)}
            onMouseLeave={(e) => removeHover(e, active)}
            onClick={() =>
              onSelectChild({
                type: "textField",
                data: tf,
                parentSub,
                parentChild,
              })
            }
          >
            {tf.heading}
          </NavLink>
        </NavItem>
      );
    });

  // ───────────── Sub Details ─────────────

  const renderSubDetails = (subDetails = [], parentChild) =>
    sortByOrder(subDetails).map((sub) => {
      const open = isOpen(sub.id, parentChild.id);
      const active = isActive(sub.id);
      const quizActive = isQuizActive(sub.id);

      const hasContent =
        sub.textFields?.length > 0 || sub.childrenSubDetails?.length > 0;
      const isExpandable = hasContent || sub.isQuiz;

      return (
        <NavItem key={sub.id}>
          <NavLink
            style={{ ...level2Style, ...(active && activeStyle) }}
            onMouseEnter={(e) => hoverEffect(e, active)}
            onMouseLeave={(e) => removeHover(e, active)}
            onClick={() => {
              if (isExpandable) toggle(sub.id, parentChild.id);
              // if (hasContent) {
              onSelectChild({ type: "subDetail", data: sub, parentChild });
              // }
            }}
          >
            <Arrow open={open} />
            <span>{sub.heading}</span>
          </NavLink>

          <Collapse isOpen={open}>
            <Nav vertical>
              {sub.textFields?.length > 0 &&
                renderTextFields(sub.textFields, sub, parentChild)}

              {sub.childrenSubDetails?.length > 0 &&
                renderSubDetails(sub.childrenSubDetails, sub)}

              {sub.isQuiz && sub.quizSlug && (
                <NavItem>
                  <NavLink
                    style={{
                      ...level3Style,
                      ...(quizActive && activeStyle),
                    }}
                    onMouseEnter={(e) => hoverEffect(e, quizActive)}
                    onMouseLeave={(e) => removeHover(e, quizActive)}
                    onClick={() => {
                      onSelectChild({
                        type: "quiz",
                        quizSlug: sub.quizSlug,
                        data: sub,
                        parentChild,
                      });

                    }}
                  >
                    <img src={icon} alt="" style={{ width: "10%" }} />{sub.heading} Quiz
                  </NavLink>
                </NavItem>
              )}
            </Nav>
          </Collapse>
        </NavItem>
      );
    });

  // ───────────── Loading ─────────────

  if (isLoading) {
    return (
      <div style={sidebarStyle} className="pt-5 text-center">
        <Spinner size="sm" color="light" />
      </div>
    );
  }

  // ───────────── Main Render ─────────────

  return (
    <div style={sidebarStyle}>
      <Nav vertical>
        {course?.children?.map((child) => {
          const open = isOpen(child.id);
          const active = isActive(child.id);
          const quizActive = isQuizActive(child.id);

          const hasContent =
            child.textFields?.length > 0 ||
            child.childrenSubDetails?.length > 0;
          const isExpandable = hasContent || child.isQuiz;

          return (
            <NavItem key={child.id}>
              <NavLink
                style={{ ...level1Style, ...(active && activeStyle) }}
                onMouseEnter={(e) => hoverEffect(e, active)}
                onMouseLeave={(e) => removeHover(e, active)}
                onClick={() => {
                  console.log("hello")
                  if (isExpandable) toggle(child.id);

                  onSelectChild({ type: "child", data: child });
                  // if (hasContent) { 
                  // }
                  // console.log("🚀 ~ CourseSidebar ~ hasContent:", hasContent)
                }}
              >
                <Arrow open={open} />
                <span>{child.heading}</span>
              </NavLink>

              <Collapse isOpen={open}>
                <Nav vertical>
                  {child.textFields?.length > 0 &&
                    renderTextFields(child.textFields, null, child)}

                  {child.childrenSubDetails?.length > 0 &&
                    renderSubDetails(child.childrenSubDetails, child)}

                  {child.isQuiz && child.quizSlug && (
                    <NavItem>
                      <NavLink
                        style={{
                          ...level3Style,
                          ...(quizActive && activeStyle),
                        }}
                        onMouseEnter={(e) => hoverEffect(e, quizActive)}
                        onMouseLeave={(e) => removeHover(e, quizActive)}
                        onClick={() => {
                          onSelectChild({
                            type: "quiz",
                            quizSlug: child.quizSlug,
                            data: child,
                          });
                          // window.location.reload();
                        }}
                      >
                        <img src={icon} alt="" style={{ width: "10%" }} />{child.heading} Quiz
                      </NavLink>
                    </NavItem>
                  )}
                </Nav>
              </Collapse>
            </NavItem>
          );
        })}
      </Nav>
    </div>
  );
}
